/**
 * ContactPage — Formulaire de contact public (Sprint E).
 *
 * Toute soumission crée un Prospect (status="contacted") + un Contact + notifie l'admin par email.
 * Supporte ?source=portal-help pour pré-remplir le message + auth pour pré-remplir nom/email/entreprise.
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { POLES } from "@/lib/poles";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send, CheckCircle2, MessageCircle } from "lucide-react";
import { useLang } from "@/context/LangContext";

const SOURCE_PRESETS = {
  "portal-help": { subject: "Demande d'aide depuis le portail membre", message: "Bonjour,\n\nJe rencontre la situation suivante depuis mon espace membre :\n\n" },
  "press-help": { subject: "Demande d'information presse", message: "Bonjour,\n\nJe suis journaliste et j'aimerais obtenir des informations sur :\n\n" },
};

export default function ContactPage() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const source = params.get("source") || "";
  const { user } = useAuth();
  const preset = SOURCE_PRESETS[source];

  const [form, setForm] = useState(() => ({
    first_name: user?.first_name || (user?.name || "").split(" ")[0] || "",
    last_name: user?.last_name || (user?.name || "").split(" ").slice(1).join(" ") || "",
    email: user?.email || "",
    phone: "",
    job_title: user?.job_title || "",
    company_name: user?.company_name || "",
    pole: "",
    message: preset?.message || "",
    consent_rgpd: !!user, // logged-in users already accepted RGPD
  }));
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_rgpd) { toast.error("Veuillez accepter les conditions RGPD"); return; }
    if (!form.email || !form.message || !form.company_name) { toast.error("Email, entreprise et message sont obligatoires"); return; }
    setSending(true);
    try {
      const payload = { ...form };
      if (source) payload.message = `[Source : ${source}]\n${payload.message}`;
      await api.post("/public/contact", payload);
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally { setSending(false); }
  };

  return (
    <PublicLayout>
      <section className="mx-auto max-w-3xl px-4 py-16 md:py-24">
        <div className="text-center">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-wine-50"><MessageCircle className="h-5 w-5 text-wine-600" /></div>
          <h1 className="mt-4 text-4xl font-bold text-navy md:text-5xl">{preset?.subject || "Contactez-nous"}</h1>
          <p className="mt-3 text-neutral-600">{t("comp_contactpage_90")}</p>
        </div>

        {sent ? (
          <div className="mt-12 rounded-3xl border border-emerald-200 bg-emerald-50/50 p-8 text-center" data-testid="contact-success">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-100"><CheckCircle2 className="h-7 w-7 text-emerald-600" /></div>
            <h2 className="mt-3 font-serif text-2xl text-emerald-900">{t("comp_contactpage_91")}</h2>
            <p className="mt-2 text-sm text-emerald-800">{t("comp_contactpage_92")}</p>
            <button onClick={() => { setSent(false); setForm({ first_name: "", last_name: "", email: "", phone: "", job_title: "", company_name: "", pole: "", message: "", consent_rgpd: false }); }} className="mt-4 text-xs font-bold text-emerald-700 underline">{t("comp_contactpage_93")}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-12 space-y-4 rounded-3xl border border-neutral-100 bg-white p-6 shadow-sm md:p-8" data-testid="contact-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder={t("comp_contactpage_94")} required className="rounded-xl border border-neutral-200 px-3 py-2.5 text-sm focus:border-wine-500 focus:outline-none" data-testid="contact-first-name" />
              <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Nom *" required className="rounded-xl border border-neutral-200 px-3 py-2.5 text-sm focus:border-wine-500 focus:outline-none" data-testid="contact-last-name" />
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" type="email" required className="rounded-xl border border-neutral-200 px-3 py-2.5 text-sm focus:border-wine-500 focus:outline-none" data-testid="contact-email" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("comp_contactpage_95")} className="rounded-xl border border-neutral-200 px-3 py-2.5 text-sm focus:border-wine-500 focus:outline-none" />
              <input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Entreprise *" required className="rounded-xl border border-neutral-200 px-3 py-2.5 text-sm focus:border-wine-500 focus:outline-none sm:col-span-2" data-testid="contact-company" />
              <input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="Fonction" className="rounded-xl border border-neutral-200 px-3 py-2.5 text-sm focus:border-wine-500 focus:outline-none" />
              <select value={form.pole} onChange={(e) => setForm({ ...form, pole: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2.5 text-sm focus:border-wine-500 focus:outline-none" data-testid="contact-pole">
                <option value="">{t("comp_contactpage_96")}</option>
                {POLES.map((p) => <option key={p.id} value={p.id}>{p.emoji} {p.label}</option>)}
              </select>
            </div>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={t("comp_contactpage_97")}
              required rows={6}
              className="w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm focus:border-wine-500 focus:outline-none"
              data-testid="contact-message"
            />
            <label className="flex items-start gap-2 text-xs text-neutral-600">
              <input type="checkbox" checked={form.consent_rgpd} onChange={(e) => setForm({ ...form, consent_rgpd: e.target.checked })} className="mt-0.5" data-testid="contact-rgpd" />
              <span>{t("comp_contactpage_98")}</span>
            </label>
            <button type="submit" disabled={sending} className="w-full rounded-full bg-wine-600 px-6 py-3 text-sm font-bold text-white hover:bg-wine-700 disabled:opacity-50" data-testid="contact-submit">
              <Send className="mr-1 inline h-4 w-4" /> {sending ? "Envoi…" : "Envoyer le message"}
            </button>
            <p className="text-center text-[10px] text-neutral-400">{t("comp_contactpage_99")} <a href="mailto:contact@lawinetech.com" className="font-bold text-wine-600">contact@lawinetech.com</a></p>
          </form>
        )}
      </section>
    </PublicLayout>
  );
}
